import React from 'react'
import PropTypes from 'prop-types'

import Select from 'Components/Form/Select'
import cn from 'comma-number'
import SvgIcon from 'Components/SvgIcon'
import Dropdown from 'Components/Dropdown'
import { _TableBody, _TableHead } from 'Components/Table/styles'

import {
  _OnlySmallScreen, _SizeRange, _SaleRentTableContainer, _PriceRange, _TableCell, _PeriodSelectorContainer, _Title,
  _SelectAndIcon, _OnlyBigScreen, _TableTHead, _StatsTitle, _StatsValue, _Dropdown, _MobileSubTitle, _TitleSubText,
} from './styles'


export const rangeFormatter = (min, max, prefix = '', postFix = '', numberFormatter, fixed = 0) => {
  const low = numberFormatter ? numberFormatter(min, fixed) : Number(min)
  const high = numberFormatter ? numberFormatter(max, fixed) : Number(max)
  if(low && high && low === high) return `$${low}${postFix}`
  return low && high ?
    `${prefix}${low}${postFix} - ${prefix}${high}${postFix}` :
    !low && high ?
      `0 - $${prefix}${high}${postFix}` :
      low && !high ?
        `${prefix}${low}${postFix}+`
        : 'NA'
}

export const stringToNumber = (num, fixed = 0) => {
  if(!num) return ''
  let n = parseFloat(num)
  n = n.toFixed(fixed)
  return cn(n)
}

// formatting data for the different type of data
export const getStatForTable = (stats) => {
  const sold = stats.Sold
  const leased = stats.Leased
  // Avg price
  const soldAvgPrice = sold && sold.avg_price
  const soldAvgSqft = sold && sold.avg_psf

  // Sold price range
  const lowPrice = sold && sold.low_price
  const highPrice = sold && sold.high_price

  // Leased price range
  const leasedLowPrice = leased && leased.low_price
  const leasedHighPrice = leased && leased.high_price

  // Sold sqft range
  const soldLowSqft = sold && sold.low_psf
  const soldHighSqft = sold && sold.high_psf

  // Leased sqft range
  const leasedLowSqft = leased && leased.low_psf
  const leasedHighSqft = leased && leased.high_psf

  const soldPriceRange =
    (rangeFormatter(lowPrice, highPrice, '$', '', stringToNumber)) || 'NA'
  const leasedPriceRange =
    (rangeFormatter(leasedLowPrice, leasedHighPrice, '$', '', stringToNumber)) || 'NA'
  const soldSqftRange = rangeFormatter(soldLowSqft, soldHighSqft, '$', '/sqft', stringToNumber) || 'NA'
  const leasedSqftRange = rangeFormatter(leasedLowSqft, leasedHighSqft, '$', '/sqft', stringToNumber, 2) || 'NA'
  return {
    soldAvgPrice,
    soldAvgSqft,
    soldPriceRange,
    soldSqftRange,
    leasedPriceRange,
    leasedSqftRange,
  }
}

const tableHeadings = [
  'Avg. Price/sqft',
  'Price Range',
  'Rent Range',
]

class SaleRentStatTable extends React.Component {
  constructor(props) {
    super(props)
    this.renderMobile = this.renderMobile.bind(this)
    this.updateStatBaseOnPeriod = this.updateStatBaseOnPeriod.bind(this)
    const targetMonthStats = props.bedTypeStats[`${12}_months`]
    const formattedStats = {
      bedTypes: [],
    }
    if(targetMonthStats) {
      formattedStats.targetMonth = targetMonthStats
      formattedStats.bedTypes = Object.keys(targetMonthStats)
    }
    this.state = {
      unitType: formattedStats?.bedTypes.map((bedType) => (bedType)),
      selectedUnitType: formattedStats?.bedTypes[0],
      months: '12',
      bedTypeStats: formattedStats,
      availablePeriods: Object.keys(props.bedTypeStats).map((month) => (month ? month.split('_')[0] : [])),
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if(prevState.months !== this.state.months) {
      this.updateStatBaseOnPeriod(this.props.bedTypeStats)
    }
    if(prevProps.bedTypeStats !== this.props.bedTypeStats && this.props.bedTypeStats) {
      const bedTypeStats = this.props.bedTypeStats
      if(bedTypeStats) {
        this.updateStatBaseOnPeriod(bedTypeStats)
      }
    }
  }

  updateStatBaseOnPeriod(bedTypeStats) {
    const targetMonthStats = bedTypeStats[`${this.state.months}_months`]
    if(targetMonthStats) {
      const formattedStats = {
        bedTypes: [],
      }
      formattedStats.targetMonth = targetMonthStats
      formattedStats.bedTypes = Object.keys(targetMonthStats)
      this.setState({
        bedTypeStats: formattedStats,
        availablePeriods: Object.keys(bedTypeStats).map((month) => (month ? month.split('_')[0] : [])),
      })
    }
  }
  renderMobile() {
    const { bedTypeStats, selectedUnitType } = this.state
    const stats = bedTypeStats.targetMonth[selectedUnitType]
    const sold = stats.Sold
    const {
      soldAvgSqft,
      soldPriceRange,
      leasedPriceRange,
      soldSqftRange,
      leasedSqftRange,
    } = getStatForTable(stats)
    return (
      <_OnlySmallScreen>
        <Dropdown
          id={'unitSelector'}
          name={'unitSeletor'}
          title={selectedUnitType}
          blurBackground={false}
          size={'large'}
        >
          {
            this.state.unitType.map(unitType => (
              <_Dropdown
                onClick={() => this.setState({ selectedUnitType: unitType })}
              >
                {unitType}
              </_Dropdown>
            ))
          }
        </Dropdown>
        <table>
          <tr>
            <_StatsTitle>Avg. Price/sqft:</_StatsTitle>
            <_StatsValue>{sold ? '$' + stringToNumber(soldAvgSqft) : 'NA'}</_StatsValue>
          </tr>
          <tr>
            <_StatsTitle>Price Range: </_StatsTitle>
            <_StatsValue>
              {soldPriceRange}
              <_MobileSubTitle>
                {soldSqftRange}
              </_MobileSubTitle>
            </_StatsValue>
          </tr>
          <tr>
            <_StatsTitle>Rented Range: </_StatsTitle>
            <_StatsValue>
              {leasedPriceRange}
              <_MobileSubTitle>
                {leasedSqftRange}
              </_MobileSubTitle>
            </_StatsValue>
          </tr>
        </table>
      </_OnlySmallScreen>
    )
  }

  render() {
    const bedTypeStats = this.state.bedTypeStats
    if(!bedTypeStats) return ''
    const { buildingName, buildingAddress } = this.props
    return (
      <_SaleRentTableContainer>
        <_Title>Sold & Rented Prices</_Title>
        <_TitleSubText>from past {this.state.months} months for {buildingName || buildingAddress} </_TitleSubText>
        {this.renderMobile()}
        <_PeriodSelectorContainer>
          <_SelectAndIcon>
            <Select
              onChange={({ value }) => this.setState({ months: value })}
              id={'periodSelector'}
              name={'periodSelector'}
              selectedVal={this.state.months}
            >
              {
                this.state.availablePeriods.map(month => (
                  <option
                    key={'month' + month}
                    value={month}
                  >
                    From Past {month} months
                  </option>
                ))
              }
            </Select>
            <SvgIcon name="#icon-arrow" width="10" height="10" />
          </_SelectAndIcon>
        </_PeriodSelectorContainer>
        <_OnlyBigScreen key={'desktop table'}>
          <_TableTHead>
            <tr>
              {
                ['Unit', ...tableHeadings].map((head) => (
                  <_TableHead key={head + 'desktop'}>{head}</_TableHead>
                ))
              }
            </tr>
          </_TableTHead>
          <_TableBody>
            {
              bedTypeStats.bedTypes.map((bedType) => {
                const stats = bedTypeStats.targetMonth[bedType]
                const sold = stats.Sold
                const {
                  soldAvgPrice,
                  soldAvgSqft,
                  soldPriceRange,
                  leasedPriceRange,
                  soldSqftRange,
                  leasedSqftRange,
                } = getStatForTable(stats)
                return (
                  <tr key={bedType + ' row'}>
                    <_TableCell width="16%">{bedType}</_TableCell>
                    <_TableCell width="28%">
                      <_PriceRange>{sold ? '$' + stringToNumber(soldAvgPrice) : 'NA'}</_PriceRange>
                      <_SizeRange>{sold ? '$' + stringToNumber(soldAvgSqft) + '/sqft' : ''}</_SizeRange>
                    </_TableCell>
                    <_TableCell width="28%">
                      <_PriceRange>
                        {soldPriceRange}
                      </_PriceRange>
                      <_SizeRange>{soldSqftRange}</_SizeRange>
                    </_TableCell>
                    <_TableCell width="28%">
                      <_PriceRange>{leasedPriceRange}</_PriceRange>
                      <_SizeRange>{leasedSqftRange}</_SizeRange>
                    </_TableCell>
                  </tr>
                )
              })
            }
          </_TableBody>
        </_OnlyBigScreen>
      </_SaleRentTableContainer>
    )
  }
}

SaleRentStatTable.propTypes = {
  bedTypeStats: PropTypes.shape({}),
  buildingAddress: PropTypes.string,
  buildingName: PropTypes.string,
}

export default SaleRentStatTable
